'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@components/ui/button';
import { cn } from 'cn';
import type { AuthPanelSlide } from './types/auth-panel-deck';
interface SlideProgressProps {
  slides: readonly AuthPanelSlide[];
  activeIndex: number;
  durationMs: number;
  /** Freezes the active bar where it is. */
  paused: boolean;
  /** False under reduced motion: bar shows full. */
  autoplay: boolean;
  onSelect: (index: number) => void;
  /** Fires when the active bar finishes filling. */
  onComplete: () => void;
}

/**
 * Story-style segments under the slides. The active one
 * fills over `durationMs` with a CSS animation, so pausing
 * is pausing the animation, and the slide advances when it
 * ends.
 */
export function SlideProgress(t0) {
  const $ = _c(18);
  const {
    slides,
    activeIndex,
    durationMs,
    paused,
    autoplay,
    onSelect,
    onComplete
  } = t0;
  let t1;
  if ($[0] !== activeIndex || $[1] !== autoplay || $[2] !== durationMs || $[3] !== onComplete || $[4] !== onSelect || $[5] !== paused || $[6] !== slides) {
    let t2;
    if ($[8] !== activeIndex || $[9] !== autoplay || $[10] !== durationMs || $[11] !== onComplete || $[12] !== onSelect || $[13] !== paused || $[14] !== slides.length) {
      t2 = (slide, index) => {
        const isActive = index === activeIndex;
        return <Button key={slide.id} variant="unstyled" size={null} aria-label={`Show slide ${index + 1} of ${slides.length}: ${slide.eyebrow}`} aria-current={isActive ? "true" : undefined} className="group h-6 flex-1 items-center rounded-full" onClick={() => onSelect(index)}><span className="bg-primary/20 group-hover:bg-primary/35 relative h-1 w-full overflow-hidden rounded-full transition-colors"><span key={isActive ? `active-${activeIndex}` : "idle"} data-testid={isActive ? "active-slide-progress" : undefined} className={cn("bg-primary absolute inset-0 origin-left rounded-full", {
              "animate-auth-slide-progress": isActive && autoplay,
              "scale-x-0": index > activeIndex,
              "scale-x-100": index < activeIndex || isActive && !autoplay
            })} style={isActive && autoplay ? {
              animationDuration: `${durationMs}ms`,
              animationPlayState: paused ? "paused" : "running"
            } : undefined} onAnimationEnd={event => {
              if (isActive && autoplay && event.target === event.currentTarget) {
                onComplete();
              }
            }} /></span></Button>;
      };
      $[8] = activeIndex;
      $[9] = autoplay;
      $[10] = durationMs;
      $[11] = onComplete;
      $[12] = onSelect;
      $[13] = paused;
      $[14] = slides.length;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    t1 = slides.map(t2);
    $[0] = activeIndex;
    $[1] = autoplay;
    $[2] = durationMs;
    $[3] = onComplete;
    $[4] = onSelect;
    $[5] = paused;
    $[6] = slides;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[16] !== t1) {
    t2 = <div className="flex w-full max-w-xs gap-2" data-sentry-component="SlideProgress" data-sentry-source-file="slide-progress.tsx">{t1}</div>;
    $[16] = t1;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
}